import { Crown, Pause, Play, SkipBack, SkipForward } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fetchMe, getCachedUser } from "../utils/auth";
import { toast } from "./ui/sonner";

export const TRACKS = [
  { title: "24-HOUR DOLO MARATHON", artist: "ENDURANCE BROADCAST", src: "/assets/radio/24-hour-dolo-marathon.mp3" },
  { title: "THE DOLO MARATHON", artist: "RUNNER'S HYMNS", src: "/assets/radio/the-dolo-marathon.mp3" },
  { title: "WALK THROUGH THE RUST", artist: "RUST BELT SIGNAL", src: "/assets/radio/walk-through-the-rust.mp3" },
  { title: "DOLO'S RUNNER OF THE SHADOWS", artist: "SHADOW FREQUENCY", src: "/assets/radio/dolos-runner-of-the-shadows.mp3" },
  { title: "DOLO'S WASTELAND KING", artist: "CROWN TRANSMISSION", src: "/assets/radio/dolos-wasteland-king.mp3" },
  { title: "DOLO'S WASTELAND MARATHON", artist: "ENDURANCE BROADCAST", src: "/assets/radio/dolos-wasteland-marathon.mp3" },
  { title: "DOLO'S WASTELAND RUN", artist: "THE RUNNER'S ANTHEM", src: "/assets/radio/dolos-wasteland-run.mp3" },
  { title: "GHOST IN THE NIGHT", artist: "NIGHT FREQUENCY TRANSMISSION", src: "/assets/radio/ghost-in-the-night.mp3" },
  { title: "WASTELAND MARATHON", artist: "ENDURANCE BROADCAST", src: "/assets/radio/wasteland-marathon.mp3" },
  { title: "DOLO'S MARATHON", artist: "ENDURANCE TRANSMISSION", src: "/assets/radio/dolos-marathon.mp3" },
  { title: "DOLO'S 0400", artist: "STATIC MASS HOUR", src: "/assets/radio/dolos-0400.mp3" },
  { title: "DOLO'S PAVEMENT", artist: "STREET SIGNAL", src: "/assets/radio/dolos-pavement.mp3" },
  { title: "DOLO", artist: "WASTELAND RADIO EXCLUSIVE", src: "/assets/radio/dolo.mp3" },
  { title: "DOLO'S WANDERING CORE", artist: "WASTELAND RADIO EXCLUSIVE", src: "/assets/radio/dolos-wandering-core.mp3" },
  { title: "DOLO'S HOMECOMING", artist: "WASTELAND RADIO EXCLUSIVE", src: "/assets/radio/dolos-homecoming.mp3" },
  { title: "DOLO'S ANTHEM", artist: "WASTELAND RADIO EXCLUSIVE", src: "/assets/radio/dolos-anthem.mp3" },
  { title: "DOLO'S MANIFESTO", artist: "THE FIELD CREED TRANSMISSION", src: "/assets/radio/dolos-manifesto.mp3" },
  { title: "DAY AND NIGHT", artist: "WASTELAND RADIO EXCLUSIVE", src: "/assets/radio/day-and-night.mp3" },
  { title: "SECTOR 7", artist: "SECTOR TRANSMISSION", src: "/assets/radio/sector-7.mp3" },
  { title: "SECTOR 8", artist: "SECTOR TRANSMISSION", src: "/assets/radio/sector-8.mp3" },
  { title: "NEON LIGHTS AND DUSTY NIGHTS", artist: "NEON FREQUENCY", src: "/assets/radio/neon-lights-and-dusty-nights.mp3" },
  { title: "WALKING THROUGH THE NEON HAZE", artist: "NEON FREQUENCY", src: "/assets/radio/walking-through-the-neon-haze.mp3" },
];

const RADIO_STATE_KEY = "dolo-radio-state";

export function loadRadioState() {
  try {
    const raw = localStorage.getItem(RADIO_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number.isInteger(parsed?.index) && parsed.index >= 0 && parsed.index < TRACKS.length && Number.isFinite(parsed?.time) && parsed.time > 0) {
      return parsed;
    }
  } catch {}
  return null;
}

export function saveRadioState(index, time) {
  try {
    localStorage.setItem(RADIO_STATE_KEY, JSON.stringify({ index, time, at: Date.now() }));
  } catch {}
}

// ---------- live listener presence ----------
const RADIO_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function getRadioSid() {
  let sid = sessionStorage.getItem("dolo-radio-sid");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replaceAll("-", "");
    sessionStorage.setItem("dolo-radio-sid", sid);
  }
  return sid;
}

export async function radioHeartbeat() {
  try {
    const res = await fetch(`${RADIO_API}/radio/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sid: getRadioSid() }),
    });
    const data = await res.json();
    return data.listeners ?? null;
  } catch {
    return null;
  }
}

export async function fetchRadioListeners() {
  try {
    const res = await fetch(`${RADIO_API}/radio/listeners`);
    const data = await res.json();
    return data.listeners ?? null;
  } catch {
    return null;
  }
}

const fmt = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export function RadioBoombox() {
  const audioRef = useRef(null);
  const restoredRef = useRef(loadRadioState());
  const [index, setIndex] = useState(() => restoredRef.current?.index ?? 0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const indexRef = useRef(index);
  const pendingSeekRef = useRef(restoredRef.current?.time ?? 0);
  const lastSaveRef = useRef(0);
  const [listeners, setListeners] = useState(0);

  // ---------- listener requests (Titan Tag accounts only) ----------
  const [requests, setRequests] = useState({ counts: {}, top: null });
  const [myRequests, setMyRequests] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("dolo-radio-requests") || "[]"));
    } catch {
      return new Set();
    }
  });
  const [user, setUser] = useState(() => getCachedUser());

  useEffect(() => {
    fetch(`${RADIO_API}/radio/requests`)
      .then((r) => r.json())
      .then((d) => setRequests({ counts: d.counts || {}, top: d.top || null }))
      .catch(() => {});
    fetchMe().then(setUser).catch(() => {});
    const onAuth = (event) => setUser(event.detail?.user ?? null);
    window.addEventListener("dolo-auth", onAuth);
    return () => window.removeEventListener("dolo-auth", onAuth);
  }, []);

  const markRequested = (src) => {
    setMyRequests((prev) => {
      const next = new Set(prev);
      next.add(src);
      try {
        localStorage.setItem("dolo-radio-requests", JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const requestTrack = async (t) => {
    if (!user) {
      toast.error("SIGN IN WITH YOUR TITAN TAG TO REQUEST A SIGNAL.");
      return;
    }
    if (myRequests.has(t.src)) {
      toast.info("ALREADY ON THE REQUEST WIRE.");
      return;
    }
    try {
      const res = await fetch(`${RADIO_API}/radio/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ src: t.src }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        toast.error("SIGN IN WITH YOUR TITAN TAG TO REQUEST A SIGNAL.");
        return;
      }
      if (!res.ok && res.status !== 409) throw new Error(data.detail || "REQUEST LOST IN STATIC — RETRY.");
      markRequested(t.src);
      setRequests((r) => {
        const counts = { ...r.counts, [t.src]: (r.counts[t.src] || 0) + (res.status === 409 ? 0 : 1) };
        const top = Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
        return { counts, top };
      });
      if (res.status === 409) toast.info("ALREADY ON THE REQUEST WIRE.");
      else toast.success(`SIGNAL REQUESTED — ${t.title}`);
    } catch (err) {
      toast.error(err.message || "REQUEST LOST IN STATIC — RETRY.");
    }
  };

  const topSrc = requests.top;
  const topTrack = topSrc ? TRACKS.find((t) => t.src === topSrc) : null;
  const topCount = topSrc ? requests.counts[topSrc] || 0 : 0;

  // live presence: heartbeat while this deck plays, poll the count either way
  useEffect(() => {
    let dead = false;
    const beat = async () => {
      const n = playing ? await radioHeartbeat() : await fetchRadioListeners();
      if (!dead && n !== null) setListeners(n);
    };
    beat();
    const timer = setInterval(beat, 30000);
    return () => {
      dead = true;
      clearInterval(timer);
    };
  }, [playing]);

  // one audio element for the deck — lean: nothing downloads until play
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;
    const onTime = () => {
      setTime(audio.currentTime);
      if (audio.currentTime - lastSaveRef.current >= 5) {
        lastSaveRef.current = audio.currentTime;
        saveRadioState(indexRef.current, audio.currentTime);
      }
    };
    const onMeta = () => {
      setDuration(audio.duration);
      if (pendingSeekRef.current > 0 && Number.isFinite(audio.duration)) {
        audio.currentTime = Math.min(pendingSeekRef.current, Math.max(0, audio.duration - 1));
        setTime(audio.currentTime);
      }
      pendingSeekRef.current = 0;
    };
    const onEnded = () => setIndex((i) => (i + 1) % TRACKS.length);
    const onHide = () => saveRadioState(indexRef.current, audio.currentTime);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnded);
    window.addEventListener("pagehide", onHide);
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnded);
      window.removeEventListener("pagehide", onHide);
    };
  }, []);

  // only one deck spins at a time — the floating chip yields to this and vice versa
  useEffect(() => {
    const onOther = (event) => {
      if (event.detail === "boombox") return;
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        saveRadioState(indexRef.current, audio.currentTime);
        audio.pause();
        setPlaying(false);
      }
    };
    window.addEventListener("dolo-radio-playing", onOther);
    return () => window.removeEventListener("dolo-radio-playing", onOther);
  }, []);

  // track change — paused deck holds off downloading until the visitor presses play
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    indexRef.current = index;
    lastSaveRef.current = 0;
    saveRadioState(index, 0);
    setTime(0);
    if (playing) {
      if (!audio.src || !audio.src.endsWith(TRACKS[index].src)) audio.src = TRACKS[index].src;
      audio.play().catch(() => setPlaying(false));
    } else if (audio.src && !audio.src.endsWith(TRACKS[index].src)) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      setDuration(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // volume change
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      saveRadioState(index, audio.currentTime);
      audio.pause();
      setPlaying(false);
    } else {
      if (!audio.src) audio.src = TRACKS[index].src;
      window.dispatchEvent(new CustomEvent("dolo-radio-playing", { detail: "boombox" }));
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const next = () => setIndex((i) => (i + 1) % TRACKS.length);
  const prev = () => setIndex((i) => (i - 1 + TRACKS.length) % TRACKS.length);

  const selectTrack = (i) => {
    const audio = audioRef.current;
    window.dispatchEvent(new CustomEvent("dolo-radio-playing", { detail: "boombox" }));
    if (i === index) {
      if (audio && !playing) {
        if (!audio.src) audio.src = TRACKS[index].src;
        audio.play().then(() => setPlaying(true)).catch(() => {});
      }
      return;
    }
    setPlaying(true);
    setIndex(i);
  };

  // rotary knob: drag vertically or scroll to turn
  const knobDrag = (event) => {
    event.preventDefault();
    const startY = event.clientY;
    const startVol = volume;
    const move = (ev) => {
      const delta = (startY - ev.clientY) / 120;
      setVolume(Math.max(0, Math.min(1, startVol + delta)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setTime(audio.currentTime);
    saveRadioState(indexRef.current, audio.currentTime);
  };

  const track = TRACKS[index];
  const knobDeg = -135 + volume * 270;

  return (
    <div className={`boombox ${playing ? "is-playing" : ""}`} data-testid="boombox">
      <div className="boombox-display" data-testid="boombox-display">
        <span className="boombox-trk">TRK {String(index + 1).padStart(2, "0")}/{String(TRACKS.length).padStart(2, "0")}</span>
        <span className="boombox-title" data-testid="boombox-track-title">
          {track.title} <em>// {track.artist}</em>
        </span>
        <span className="boombox-time" data-testid="boombox-time">
          {fmt(time)} / {fmt(duration)}
        </span>
        <span className="boombox-live" data-testid="boombox-listeners">
          <i aria-hidden="true" />
          RUNNERS TUNED IN: {String(listeners).padStart(2, "0")}
        </span>
      </div>

      <div className="boombox-progress" onClick={seek} data-testid="boombox-progress" role="slider" aria-label="Seek" aria-valuenow={Math.round((time / (duration || 1)) * 100)}>
        <span style={{ width: `${(time / (duration || 1)) * 100}%` }} />
      </div>

      <div className="boombox-controls">
        <button type="button" className="boombox-btn" onClick={prev} data-testid="boombox-prev-button" aria-label="Previous track">
          <SkipBack size={17} />
        </button>
        <button type="button" className="boombox-btn boombox-play" onClick={toggle} data-testid="boombox-play-button" aria-label={playing ? "Pause" : "Play"}>
          {playing ? <Pause size={19} /> : <Play size={19} />}
        </button>
        <button type="button" className="boombox-btn" onClick={next} data-testid="boombox-next-button" aria-label="Next track">
          <SkipForward size={17} />
        </button>

        <div className="boombox-vu" data-testid="boombox-vu" aria-hidden="true">
          {Array.from({ length: 10 }, (_, i) => (
            <i key={i} style={{ animationDelay: `${i * 0.09}s` }} />
          ))}
        </div>

        <div className="boombox-knob-wrap">
          <div
            className="boombox-knob-ring"
            style={{ background: `conic-gradient(from -135deg, var(--crimson) 0deg ${volume * 270}deg, rgba(255, 0, 51, 0.12) ${volume * 270}deg 270deg, transparent 270deg 360deg)` }}
          >
            <div
              className="boombox-knob"
              role="slider"
              aria-label="Volume"
              aria-valuenow={Math.round(volume * 100)}
              data-testid="boombox-knob"
              onPointerDown={knobDrag}
              onWheel={(e) => setVolume((v) => Math.max(0, Math.min(1, v - Math.sign(e.deltaY) * 0.07)))}
            >
              <span className="boombox-knob-marker" style={{ transform: `rotate(${knobDeg}deg)` }} />
            </div>
          </div>
          <small>VOL</small>
        </div>
      </div>

      <div className="boombox-tracklist" data-testid="boombox-tracklist">
        <div className={`boombox-wanted-bar ${topTrack ? "" : "is-empty"}`} data-testid="radio-most-wanted">
          {topTrack ? (
            <>
              <Crown size={13} /> MOST WANTED SIGNAL — {topTrack.title} · {topCount} {topCount === 1 ? "REQUEST" : "REQUESTS"}
            </>
          ) : (
            <>NO REQUESTS ON THE WIRE — TITAN TAG HOLDERS PICK THE NEXT ANTHEM</>
          )}
        </div>
        {TRACKS.map((t, i) => (
          <div className="boombox-track-row" key={t.src}>
            <button
              type="button"
              className={`boombox-track ${i === index ? "is-active" : ""}`}
              onClick={() => selectTrack(i)}
              data-testid={`boombox-track-${i}`}
            >
              <span className="boombox-track-num">{String(i + 1).padStart(2, "0")}</span>
              <span className="boombox-track-name">
                {t.title}
                <em>{t.artist}</em>
              </span>
              {topSrc === t.src && (
                <span className="boombox-crown" data-testid="boombox-most-wanted">
                  <Crown size={11} /> MOST WANTED
                </span>
              )}
              {i === index && <span className="boombox-onair">ON AIR</span>}
            </button>
            <button
              type="button"
              className={`boombox-req ${myRequests.has(t.src) ? "is-sent" : ""}`}
              onClick={() => requestTrack(t)}
              data-testid={`boombox-req-${i}`}
              aria-label={`Request ${t.title}`}
              title={user ? "Request this signal" : "Sign in with your Titan Tag to request"}
            >
              {myRequests.has(t.src) ? "SENT" : "REQ"}
              {(requests.counts[t.src] || 0) > 0 && <em>{requests.counts[t.src]}</em>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
